import {useBlockFilterOptionsQuery} from '@/generated/graphql';
import {useFeatures} from '@/hooks/useFeatures/useFeatures';
import {edgesToNodes} from '@ark/react';
import {useSelectedCommunities} from '@ark/web';
import {useCallback, useEffect, useMemo} from 'react';
import {useLocalStorage} from 'usehooks-ts';

export type SelectedBlock = {
  id: string;
  name: string;
  communityId: string;
};

export type BlockFilterOption = SelectedBlock & {
  communityName: string;
  isDefault: boolean;
};

export const useBlockFilter = () => {
  const [features] = useFeatures();
  const enabled = Boolean(features.AutomatedBlocks);
  const communities = useSelectedCommunities();
  const [storedBlock, setStoredBlock] = useLocalStorage<SelectedBlock | null>(
    'blockFilter',
    null,
  );

  const {data, fetchMore} = useBlockFilterOptionsQuery({
    variables: {communities},
    skip: !enabled || communities.length === 0,
  });

  const hasNextPage = data?.blocks?.pageInfo.hasNextPage;
  const endCursor = data?.blocks?.pageInfo.endCursor;

  useEffect(() => {
    if (hasNextPage && endCursor) {
      fetchMore({variables: {after: endCursor}});
    }
  }, [hasNextPage, endCursor, fetchMore]);

  const blocks: BlockFilterOption[] = useMemo(
    () =>
      edgesToNodes(data?.blocks?.edges)
        .map(block => ({
          id: block.id,
          name: block.name,
          isDefault: block.isDefault,
          communityId: block.community.id,
          communityName: block.community.name ?? '',
        }))
        .sort(
          (a, b) =>
            a.communityName.localeCompare(b.communityName) ||
            Number(b.isDefault) - Number(a.isDefault) ||
            a.name.localeCompare(b.name),
        ),
    [data],
  );

  const allLoaded = Boolean(data) && !hasNextPage;
  const storedBlockAvailable =
    storedBlock !== null &&
    communities.includes(storedBlock.communityId) &&
    (!allLoaded || blocks.some(block => block.id === storedBlock.id));
  const selectedBlock = enabled && storedBlockAvailable ? storedBlock : null;

  const setSelectedBlock = useCallback(
    (block: SelectedBlock | null) => {
      setStoredBlock(
        block && {
          id: block.id,
          name: block.name,
          communityId: block.communityId,
        },
      );
    },
    [setStoredBlock],
  );

  return {enabled, blocks, selectedBlock, setSelectedBlock};
};
